import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Stack, Button, Chip, CircularProgress, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  InputAdornment, Pagination, Autocomplete, Tooltip, FormControlLabel, Switch,
} from '@mui/material';
import {
  Search as SearchIcon, ArrowBack as BackIcon, AutoFixHigh as AutoIcon,
  CheckCircle as ConfirmIcon, Close as DismissIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import fundedAPI from '../../api/funded';
import { currency } from '../../components/funded/format';

// Per-row candidate picker: a dropdown of fuzzy candidates + manual client search.
function CandidatePicker({ lead, value, onChange }) {
  const [options, setOptions] = useState(lead.candidates || []);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!input || input.length < 2) { setOptions(lead.candidates || []); return; }
    let active = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fundedAPI.getClients({ search: input, limit: 15 });
        if (active) setOptions((res.data.data || []).map((c) => ({ client_id: c.client_id, business_name: c.business_name, score: null })));
      } finally { if (active) setLoading(false); }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [input, lead.candidates]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      getOptionLabel={(o) => o?.business_name || ''}
      isOptionEqualToValue={(o, v) => o.client_id === v?.client_id}
      loading={loading}
      renderOption={(props, o) => (
        <li {...props} key={o.client_id}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Typography variant="body2">{o.business_name}</Typography>
            {o.score != null && (
              <Chip label={o.exact ? 'exact' : `${Math.round(o.score * 100)}%`} size="small"
                color={o.exact ? 'success' : o.score >= 0.6 ? 'info' : 'default'} sx={{ height: 18, fontSize: '0.6rem', ml: 1 }} />
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} placeholder="Pick or search client…" sx={{ minWidth: 240 }} />
      )}
    />
  );
}

export default function LeadMatchesView() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [selections, setSelections] = useState({}); // leadId -> candidate
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [autoRunning, setAutoRunning] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fundedAPI.getLeadMatches({ search, page, limit: 25, onlyCandidates: showAll ? 'false' : 'true' });
      const data = res.data.data || [];
      setRows(data);
      setMeta({ total: res.data.total, page: res.data.page, pages: res.data.pages });
      // Default-select the auto-match (exact single) or the top candidate.
      setSelections((prev) => {
        const next = { ...prev };
        for (const l of data) {
          if (next[l._id]) continue;
          const auto = l.autoMatch ? l.candidates.find((c) => c.client_id === l.autoMatch) : null;
          next[l._id] = auto || l.candidates?.[0] || null;
        }
        return next;
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, page, showAll]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const confirm = async (lead) => {
    const sel = selections[lead._id];
    if (!sel) return toast.error('Pick a client first');
    setBusyId(lead._id);
    try {
      await fundedAPI.confirmLeadMatch(lead._id, { client_id: sel.client_id });
      toast.success(`Matched to ${sel.business_name}`);
      setRows((prev) => prev.filter((r) => r._id !== lead._id));
      setMeta((m) => ({ ...m, total: Math.max(0, m.total - 1) }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to confirm');
    } finally { setBusyId(null); }
  };

  const dismiss = async (lead) => {
    setBusyId(lead._id);
    try {
      await fundedAPI.dismissLeadMatch(lead._id);
      toast.info('Dismissed — kept as a lead');
      setRows((prev) => prev.filter((r) => r._id !== lead._id));
      setMeta((m) => ({ ...m, total: Math.max(0, m.total - 1) }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to dismiss');
    } finally { setBusyId(null); }
  };

  const autoConfirm = async () => {
    if (!window.confirm('Auto-confirm all leads whose name exactly matches a single funded client?')) return;
    setAutoRunning(true);
    try {
      const res = await fundedAPI.autoConfirmLeadMatches();
      toast.success(`Auto-confirmed ${res.data.confirmed} exact match${res.data.confirmed === 1 ? '' : 'es'}`);
      setPage(1);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Auto-confirm failed');
    } finally { setAutoRunning(false); }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Button startIcon={<BackIcon />} size="small" onClick={() => navigate('/sales')} sx={{ mb: 0.5 }}>Back to Sales CRM</Button>
          <Typography variant="h5" fontWeight={800} color="#0d2137">Funded-Client Matches</Typography>
          <Typography variant="body2" color="text.secondary">
            Imported leads that may already be funded clients — confirm to link &amp; enrich the client, or dismiss to keep as a lead.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<AutoIcon />} onClick={autoConfirm} disabled={autoRunning || meta.total === 0}>
          {autoRunning ? 'Working…' : 'Auto-confirm exact'}
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small" placeholder="Search leads by name, EIN, email…"
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          sx={{ width: { xs: '100%', sm: 340 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Chip label={`${meta.total} to review`} color={meta.total ? 'warning' : 'success'} />
        <FormControlLabel
          control={<Switch size="small" checked={showAll} onChange={(e) => { setShowAll(e.target.checked); setPage(1); }} />}
          label={<Typography variant="body2">Show all imported leads</Typography>}
        />
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={700}>🎉 Nothing to review</Typography>
          <Typography variant="body2" color="text.secondary">No imported leads are awaiting a funded-client match.</Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Lead (imported)', 'EIN', 'Requested', 'Match to funded client', ''].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((l) => (
                  <TableRow key={l._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ maxWidth: 240 }}>{l.businessName || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{l.email || ''}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{l.businessEIN || '—'}</Typography></TableCell>
                    <TableCell>{l.requestedAmount != null ? currency(l.requestedAmount) : '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CandidatePicker lead={l} value={selections[l._id] || null} onChange={(v) => setSelections((p) => ({ ...p, [l._id]: v }))} />
                        {l.autoMatch && <Chip label="exact" size="small" color="success" sx={{ height: 18, fontSize: '0.6rem' }} />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Confirm match (link & enrich client, archive lead)">
                          <span>
                            <Button size="small" variant="contained" color="success" startIcon={<ConfirmIcon />}
                              disabled={busyId === l._id || !selections[l._id]} onClick={() => confirm(l)}>Confirm</Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Not a match — keep as a lead">
                          <span>
                            <Button size="small" variant="outlined" color="inherit" startIcon={<DismissIcon />}
                              disabled={busyId === l._id} onClick={() => dismiss(l)}>Dismiss</Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
            {meta.pages > 1 && <Pagination count={meta.pages} page={meta.page} onChange={(_, p) => setPage(p)} size="small" />}
          </Stack>
        </>
      )}
    </Container>
  );
}
